import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, CheckCircle2, Clock, FileCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";

const STAGES = [
    { key: "received", label: "Submission Received", icon: FileCheck },
    { key: "payment_verified", label: "Payment Verified", icon: CheckCircle2 },
    { key: "under_review", label: "Under Review", icon: Clock },
    { key: "report_ready", label: "Report Ready", icon: FileCheck },
    { key: "plan_delivered", label: "Plan Delivered", icon: Send },
    { key: "completed", label: "Completed", icon: CheckCircle2 },
];

const pkgLabel = { disha: "Disha", niti: "Niti", samvad: "Samvad" };

export default function TrackSubmission() {
    const [ref, setRef] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const submit = async (e) => {
        e.preventDefault();
        if (!ref.trim() || !email.trim()) {
            toast.error("Enter both reference ID and email.");
            return;
        }
        setLoading(true);
        try {
            const { data: d } = await api.get(
                `/track/${ref.trim()}?email=${encodeURIComponent(email.trim())}`,
            );
            setData(d);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Could not find a matching submission.",
            );
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    const currentStageIndex = data
        ? STAGES.findIndex((s) => s.key === data.status)
        : -1;

    return (
        <div className="bg-[#FDFAF5] min-h-screen">
            <Navbar />

            <section className="pt-32 md:pt-40 pb-16">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <div className="flex items-center gap-3 mb-4">
                        <span className="ornament" />
                        <span className="text-xs tracking-[0.25em] uppercase text-[#1A5C53] font-medium">
                            Client Portal
                        </span>
                    </div>
                    <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B1A] leading-tight">
                        Track your DhanNniti submission.
                    </h1>
                    <p className="mt-4 text-[#4A5A58]">
                        Enter the reference ID you received after submitting
                        your questionnaire, along with the email you used.
                    </p>

                    <form
                        onSubmit={submit}
                        className="mt-10 bg-white border border-[#E6DFD3] rounded-2xl p-6 md:p-8 space-y-5"
                    >
                        <div>
                            <Label>Reference ID</Label>
                            <Input
                                data-testid="track-ref-input"
                                value={ref}
                                onChange={(e) => setRef(e.target.value)}
                                placeholder="e.g. 5f2e5a2b-..."
                                className="mt-1.5 bg-[#FDFAF5] border-[#E6DFD3] font-mono text-sm"
                            />
                        </div>
                        <div>
                            <Label>Email</Label>
                            <Input
                                type="email"
                                data-testid="track-email-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="mt-1.5 bg-[#FDFAF5] border-[#E6DFD3]"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="track-submit-btn"
                            className="inline-flex items-center gap-2 bg-[#1A5C53] text-white hover:bg-[#13423B] disabled:opacity-60 rounded-full px-6 py-3 font-medium"
                        >
                            <Search size={16} />{" "}
                            {loading ? "Searching…" : "Check Progress"}
                        </button>
                    </form>

                    {data && (
                        <div
                            data-testid="track-result"
                            className="mt-10 bg-white border border-[#E6DFD3] rounded-2xl p-6 md:p-10"
                        >
                            <div className="flex items-start justify-between flex-wrap gap-3 pb-6 border-b border-[#E6DFD3]">
                                <div>
                                    <div className="text-xs tracking-[0.25em] uppercase text-[#D4944C]">
                                        {pkgLabel[data.package] || data.package}
                                    </div>
                                    <div className="font-serif text-2xl text-[#0D1B1A] mt-1">
                                        Hello, {data.name?.split(" ")[0]} 👋
                                    </div>
                                </div>
                                <div className="text-xs text-[#758684]">
                                    Submitted{" "}
                                    {new Date(data.created_at).toLocaleDateString(
                                        "en-IN",
                                        {
                                            day: "numeric",
                                            month: "short",
                                            year: "numeric",
                                        },
                                    )}
                                </div>
                            </div>

                            <div className="mt-8">
                                <h2 className="font-serif text-xl text-[#0D1B1A] mb-6">
                                    Progress
                                </h2>
                                <ol className="space-y-5">
                                    {STAGES.map((s, idx) => {
                                        const done = idx <= currentStageIndex;
                                        const active = idx === currentStageIndex;
                                        const Icon = s.icon;
                                        return (
                                            <li
                                                key={s.key}
                                                className="flex items-start gap-4"
                                            >
                                                <div
                                                    className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                        done
                                                            ? "bg-[#1A5C53] text-white"
                                                            : "bg-[#F3EFE9] text-[#758684]"
                                                    }`}
                                                >
                                                    <Icon size={16} />
                                                </div>
                                                <div className="flex-1 pt-1.5">
                                                    <div
                                                        className={`font-medium ${
                                                            active
                                                                ? "text-[#1A5C53]"
                                                                : done
                                                                  ? "text-[#0D1B1A]"
                                                                  : "text-[#758684]"
                                                        }`}
                                                    >
                                                        {s.label}
                                                    </div>
                                                    {active && (
                                                        <div className="text-xs text-[#4A5A58] mt-1">
                                                            Current stage
                                                        </div>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ol>
                            </div>

                            {data.progress_notes && (
                                <div className="mt-8 bg-[#F3EFE9]/60 border border-[#E6DFD3] rounded-xl p-5">
                                    <div className="text-xs uppercase tracking-[0.2em] text-[#758684] mb-2">
                                        Note from your strategist
                                    </div>
                                    <p className="text-[#0D1B1A] whitespace-pre-wrap">
                                        {data.progress_notes}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </div>
    );
}
